import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { SmartLinkStatsMediaItem } from 'fym-shared/src/smartlinks/stats';
import { TypedMemo } from '../../../common/types';
import { mediaStatsMessages } from './mediaStatsMessages';
import { shareOf } from './mediaStatsRows';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Media panel; each part uses its own slice of it */
import styles from './SMLMediaPanel.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface SMLMediaFunnelProps {
  item: SmartLinkStatsMediaItem;
}

/** How far the plays got (design c1): Started, 25 %, 50 %, 75 %, Finished, each as a share of the plays. */
const SMLMediaFunnelComponent: React.FC<SMLMediaFunnelProps> = ({ item }) => {
  const intl = useIntl();

  const steps = useMemo(() => {
    const [q25, q50, q75, q100] = item.quartiles;
    return [
      { key: 'started', label: intl.formatMessage(mediaStatsMessages.started), count: item.plays },
      { key: 'q25', label: '25%', count: q25 },
      { key: 'q50', label: '50%', count: q50 },
      { key: 'q75', label: '75%', count: q75 },
      { key: 'q100', label: intl.formatMessage(mediaStatsMessages.finished), count: q100 },
    ].map((step) => ({ ...step, share: Math.min(1, shareOf(step.count, item.plays) ?? 0) }));
  }, [intl, item]);

  return (
    <div className={styles.funnel} aria-label={intl.formatMessage(mediaStatsMessages.funnelLabel)} role="list">
      {steps.map((step) => (
        <div key={step.key} className={styles.funnelRow} role="listitem" data-testid={`media-funnel-${step.key}`}>
          <span className={styles.funnelLabel}>{step.label}</span>
          <span className={styles.funnelTrack}>
            <span className={styles.funnelBar} style={{ width: `${step.share * 100}%` }} />
          </span>
          <span className={styles.funnelCount}>{intl.formatNumber(step.count, { notation: 'compact' })}</span>
          <span className={styles.funnelShare}>
            {intl.formatNumber(step.share, { style: 'percent', maximumFractionDigits: 0 })}
          </span>
        </div>
      ))}
    </div>
  );
};

export const SMLMediaFunnel = TypedMemo(SMLMediaFunnelComponent);
